import ImageOptimizer from '@/components/image-optimizer';
import Pagination from '@/components/pagination';
import { Z_INDEX } from '@/constants';
import { useStoreReviewList } from '@/services/query/store-review.query';
import { StoreReviewEntity } from '@/services/types/store-review.type';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface Props {
  storeId: number;
  storeName: string;
  open: boolean;
  onClose: () => void;
  children?: React.ReactNode;
}

export default function ReviewModal({
  storeId,
  storeName,
  open,
  onClose,
}: Props) {
  const LIMIT = 10;
  const [page, setPage] = useState(1);
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const { data: reviewList } = useStoreReviewList({
    storeId,
    limit: LIMIT,
    page,
  });

  const reviews: StoreReviewEntity[] = reviewList?.data.reviews ?? [];
  const totalCount = reviewList?.data.totalCount || 0;
  const totalPages = LIMIT && LIMIT > 0 ? Math.ceil(totalCount / LIMIT) : 0;

  useEffect(() => {
    if (open) {
      // 현재 스크롤 위치 저장
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.overflowY = 'scroll';
      document.body.style.width = '100%';
      return () => {
        // 해제 시 원래 위치로 복귀
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.overflowY = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="z- fixed inset-0 flex items-center justify-center"
      onClick={onClose}
      style={{
        zIndex: Z_INDEX.Modal,
      }}
    >
      {/* Backdrop - only visible on desktop */}
      <div className="absolute inset-0 block bg-black/40" />

      {/* 모달 내부 클릭 시 닫히지 않게*/}
      <div
        className="desktop:max-h-[800px] desktop:max-w-[1020px] desktop:min-h-[600px] desktop:h-fit desktop:w-[1020px] desktop:overflow-hidden desktop:rounded-2xl desktop:shadow-lg relative flex h-screen w-full max-w-[500px] flex-col bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b-stroke-white sticky inset-0 z-1 flex w-full justify-between border-b bg-white px-7">
          <div className="flex h-20 items-center gap-1">
            <h1 className="text-level-2 typo-26 shrink-0 font-extrabold">
              리뷰
            </h1>
            <span className="text-level-6 typo-26 shrink-0 font-extrabold">
              {totalCount}
            </span>
            <span className="bg-stroke-white mx-1 size-1 shrink-0 rounded-full"></span>
            <span className="border-stroke-white typo-16 text-main-orange flex items-center rounded-full border px-3 py-2 font-bold">
              <ImageOptimizer
                src="/images/icons/icon_map_pin.svg"
                alt="위치"
                width={16}
                height={16}
              />
              {storeName}
            </span>
          </div>
          <button
            className="text-level-2 shrink-0 cursor-pointer"
            onClick={onClose}
          >
            <X size={32} />
          </button>
        </div>
        <div className="divide-stroke-white custom-scrollbar flex flex-col divide-y overflow-y-auto px-7">
          {reviews?.map((review, index) => (
            <div key={review.id || index} className="flex flex-col gap-4 pt-7">
              <div className="typo-20 text-level-1 flex items-center gap-1.5 font-semibold">
                <div className="relative size-8">
                  <ImageOptimizer
                    key={index}
                    src="/images/branches/profile_m.svg"
                    alt={'대한민국히트브랜드 대상'}
                    fill
                  />
                </div>
                {review.username}
              </div>
              <div className="flex gap-3">
                {review.images?.map((image) => (
                  <div
                    key={image.id}
                    className="relative size-30 overflow-hidden rounded-xl"
                  >
                    <ImageOptimizer
                      src={image.filePath}
                      alt="리뷰 이미지"
                      fill
                      className="object-cover"
                    />
                  </div>
                ))}
              </div>
              <div className="text-level-3 typo-15 mb-7 leading-snug font-medium">
                {review.reviewText}
              </div>
            </div>
          ))}
        </div>
        {/* {children} */}

        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          className="desktop:!pb-10 desktop:!pt-10 sticky bottom-0 left-0 z-1 !h-10 w-full bg-white !pt-8 !pb-8"
        />
      </div>
    </div>
  );
}
